import { Inject } from '@odinjs/odin';

import { Endpoint, ProviderAccreditationPendenciesService, ProviderAccreditationStatusService, Response, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-provider-components';
import { ActionAPI, FetchAPI, FieldAPI, FormAPI, LegendsAPI, LocatorAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnBeforeFetch, OnBlur, OnCancel, OnChange, OnClick, OnClose, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { FragmentNames } from '../../codegen';
import { AccreditationProceduresAliases as Aliases } from '../../codegen/constants/aliases/accreditation-procedures-aliases';
import BaseController from '../BaseController';

import { AccreditationStatus, AccreditationSteps } from './accreditation-update-controller';
import { ActionTypes } from './update-accreditation-type';

interface Procedure {
  nrSequence?: number,
  cdSpecialty?: number,
  locatorProcedure?: TypeProcedure,
  vlProcedure: number,
  vlMedical: number,
  vlFilm: number,
  vlOperationalCost: number,
  procedureValue: number,
  ieAction: ActionTypes,
  dsAction: string,
}

interface TypeProcedure {
  cdProcedure?: number,
  dsProcedure?: string,
  ieSourceProcedure?: number,
}

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};

const NR_SEQ_PROCEDURE = 'nrSequence';

@Controller({ fragment: FragmentNames.ACCREDITATION_PROCEDURES })
class AccreditationProceduresController extends BaseController {
  @Inject(Aliases.FETCH_LIST)
  private fetchList: FetchAPI;
  @Inject(Aliases.PANEL_MODAL)
  private panelModal: PanelAPI;
  @Inject(Aliases.FORM_PROCEDURE)
  private formProcedure: FormAPI;
  @Inject(Aliases.LOCATOR_PROCEDURE)
  private locatorProcedure: LocatorAPI;
  @Inject(Aliases.FETCH_PROCEDURE)
  private fetchProcedure: FetchAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toastNotificationPendencies: ToastNotificationAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.FIELD_VALUE_PROCEDURE)
  private fieldValueProcedure: FieldAPI;
  @Inject(Aliases.FIELD_OPERATIONAL_COST)
  private fieldOperationalCost: FieldAPI;
  @Inject(Aliases.FIELD_MEDICAL_VALUE)
  private fieldMedicalValue: FieldAPI;
  @Inject(Aliases.FIELD_FILM_VALUE)
  private fieldFilmValue: FieldAPI;
  @Inject(Aliases.LEGENDS)
  private legends: LegendsAPI;
  @Inject(Aliases.FIELD_ORIGIN)
  private fieldOrigin: FieldAPI;

  @OnAfterFetch(Aliases.FETCH_LIST)
  onAfterFetchList(event: FetchEvent): void {
    const { data } = event;
    if (isEmptyArrayOrObject(data)) {
      this.legends.setShowExpression('');
      this.legends.setHideExpression('');
    } else {
      this.legends.setShowExpression(MessageService.translate('tws.legends.show'));
      this.legends.setHideExpression(MessageService.translate('tws.legends.hide'));
    }
  }

  @OnChange(Aliases.FIELD_SPECIALTY)
  onChangeSpecialty(event: ChangeEvent<string>): void {
    const cdSpecialty = event.value;
    this.context.set({ cdSpecialty });
  }

  @OnChange(Aliases.FIELD_CODE)
  onChangeCode(event: ChangeEvent<string>): void {
    const cdProcedure = event.value;
    if (cdProcedure && cdProcedure.length > 0) {
      this.fieldOrigin.setValidators([{ name: 'required', options: {} }] as Validator[]);
    } else {
      this.fieldOrigin.setValidators([]);
    }
  }

  @OnMount([
    Aliases.FIELD_VALUE_PROCEDURE_LIST, Aliases.FIELD_VALUE_MEDICAL_LIST,
    Aliases.FIELD_FILM_VALUE_LIST, Aliases.FIELD_OPERATIONAL_COST_VALUE_LIST,
  ])
  onMountFieldValue(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const { preLoadedProcedure } = api.getData();
    api.setVisible(preLoadedProcedure == 'N');
  }

  @OnMount(Aliases.ACTION_ADD_PROCEDURE)
  onMountPanelForm(event: MountEvent): void {
    const api = event.target as ActionAPI;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;

    ProviderAccreditationStatusService.getAccreditationStatus(Number(nrSeqAccreditation), Number(nrSeqProvider))
      .then((response) => {
        const { status } = response.body as unknown as Response;
        api.setEnabled(status == AccreditationStatus.AWAITING_REQUESTER || status == AccreditationStatus.AWAITING_REVISION);
      }).catch((response) => ToastService.open(response));
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.PROCEDURES);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toastNotificationPendencies, event);
    }
  }

  @OnClick(Aliases.ACTION_ADD_PROCEDURE)
  onClickAdd(): void {
    const { nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.context.set({ nrSeqProvider, nrSeqTypeAccreditation, nrSequence: null });
    this.panelModal.setVisible(true);
  }

  @OnClose(Aliases.MODAL_ADD_NEW)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  private cleanForm(): void {
    this.formProcedure.setValue({});
    this.locatorProcedure.setReadOnly(false);
    this.context.set({ nrSequence: null });
  }

  private refreshList(): void {
    const cdSpecialty = this.context.get('cdSpecialty');
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.fetchList.setParameters({ nrSeqProvider, nrSeqAccreditation, cdSpecialty });
    this.fetchList.forceFetch();
  }

  @OnBlur([
    Aliases.FIELD_OPERATIONAL_COST, Aliases.FIELD_MEDICAL_VALUE,
    Aliases.FIELD_FILM_VALUE])
  onChangeValues(): void {
    const { vlOperationalCost } = this.fieldOperationalCost.getData() as unknown as Procedure;
    const { vlMedical } = this.fieldMedicalValue.getData() as unknown as Procedure;
    const { vlFilm } = this.fieldFilmValue.getData() as unknown as Procedure;

    const total = vlOperationalCost + vlMedical + vlFilm;
    this.fieldValueProcedure.setValue(total);
  }

  @OnSubmit(Aliases.FORM_PROCEDURE)
  async onSaveActionClick(): Promise<void> {
    await this.saveRecord();
  }

  private async saveRecord(): Promise<void> {
    const record = this.formProcedure.getRecord() as unknown as Procedure;
    const cdSpecialty = this.context.get('cdSpecialty');
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { locatorProcedure } = record;
    await HttpService.post(Endpoint.POST.ACCREDITATION_PROCEDURE, { nrSeqProvider, nrSeqAccreditation }, {
      body: {
        ...record, cdProcedure: locatorProcedure?.cdProcedure,
        ieSourceProcedure: locatorProcedure?.ieSourceProcedure, nrSeqAccreditation, cdSpecialty,
      },
    }).then(() => {
      this.cleanForm();
      this.refreshList();
      this.panelModal.setVisible(false);
    }).catch((response) => ToastService.open(response));
  }

  @OnCancel(Aliases.FORM_PROCEDURE)
  onCleanForm(): void {
    this.cleanForm();
  }

  @OnBeforeFetch(Aliases.FETCH_PROCEDURE)
  onBeforeFetchProcedure(): void {
    const nrSequence = this.context.get(NR_SEQ_PROCEDURE);
    const { nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.context.set({ nrSeqProvider, nrSeqTypeAccreditation });
    this.fetchProcedure.setParameters({ nrSequence });
  }

  @OnAfterFetch(Aliases.FETCH_PROCEDURE)
  onAfterFetchProcedure(): void {
    const nrSequence = this.context.get(NR_SEQ_PROCEDURE);
    this.locatorProcedure.setReadOnly(!!nrSequence);
  }

  @HPMSOnCreateMenu(Aliases.MY_DATA_CARD)
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { editable } = data;
    shouldRender(editable == 'S');
  }

  @HPMSOnCreateOption(Aliases.MY_DATA_CARD)
  onCreateOptionMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { editable, preLoadedProcedure } = data;
    const show = {
      [OptionKey.EDIT]: editable == 'S' && preLoadedProcedure == 'N',
      [OptionKey.DELETE]: true,
    };
    shouldRender(show[key]);
  }

  @HPMSOnOptionClick(Aliases.MY_DATA_CARD)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSequence } = data as unknown as Procedure;

    const action = {
      [OptionKey.EDIT]: () => this.editRecord(Number(nrSequence)),
      [OptionKey.DELETE]: () => this.deleteRecord(Number(nrSequence)),
    };

    if (action[key]) {
      action[key]();
    }
  }

  private editRecord(nrSequence: number): void {
    this.context.set({ nrSequence });
    this.panelModal.setVisible(true);
    this.fetchProcedure.forceFetch();
  }

  private async deleteRecord(nrSequence: number): Promise<void> {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    await HttpService.delete(Endpoint.DELETE.ACCREDITATION_PROCEDURE, { nrSeqProvider, nrSequence, nrSeqAccreditation }, { body: { nrSeqAccreditation } })
      .then(() => {
        this.refreshList();
      }).catch((response) => ToastService.open(response));
  }
}

export {
  AccreditationProceduresController,
};

